import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useSyncExternalStore,
} from 'react'

import { createPersistedPreference } from '@/lib/hooks/createPersistedPreference'
import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'
import {
  KNOWLEDGE_BASE_HOME,
  formatKnowledgeBaseKey,
  knowledgeBaseEvents,
  parseKnowledgeBaseKey,
} from '@/lib/misc/knowledgeBase'
import { isApplePlatform } from '@/lib/misc/Utils'

export const KNOWLEDGE_BASE_MIN_WIDTH = 360
export const KNOWLEDGE_BASE_MAX_WIDTH = 820
export const KNOWLEDGE_BASE_DEFAULT_WIDTH = 460
export const KNOWLEDGE_BASE_DOCK_MEDIA_QUERY = '(min-width: 1280px)'
export const KNOWLEDGE_BASE_QUERY_PARAM = 'kb'
export const KNOWLEDGE_BASE_SHORTCUT_LABEL = isApplePlatform()
  ? '⌘ /'
  : 'Ctrl /'
const RECENT_LIMIT = 6

export const clampKnowledgeBaseWidth = (width: number): number =>
  Math.round(
    Math.min(
      KNOWLEDGE_BASE_MAX_WIDTH,
      Math.max(KNOWLEDGE_BASE_MIN_WIDTH, width)
    )
  )

const dockedPreference = createPersistedPreference<boolean>({
  key: 'titan.knowledge-base-docked',
  fallback: false,
  parse: (raw) => (raw === 'true' ? true : raw === 'false' ? false : undefined),
})

const widthPreference = createPersistedPreference<number>({
  key: 'titan.knowledge-base-width',
  fallback: KNOWLEDGE_BASE_DEFAULT_WIDTH,
  parse: (raw) => {
    const width = Number(raw)
    return Number.isFinite(width) ? clampKnowledgeBaseWidth(width) : undefined
  },
})

const recentPreference = createPersistedPreference<string>({
  key: 'titan.knowledge-base-recent',
  fallback: '',
  parse: (raw) => raw,
})

const subscribeToDockMedia = (onChange: () => void) => {
  const media = window.matchMedia(KNOWLEDGE_BASE_DOCK_MEDIA_QUERY)
  media.addEventListener('change', onChange)
  return () => media.removeEventListener('change', onChange)
}

const readDockMedia = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(KNOWLEDGE_BASE_DOCK_MEDIA_QUERY).matches

const useCanDock = () =>
  useSyncExternalStore(subscribeToDockMedia, readDockMedia, () => false)

interface HistoryState {
  stack: string[]
  index: number
}

type HistoryAction =
  | { type: 'push'; key: string }
  | { type: 'replace'; key: string }
  | { type: 'go'; delta: number }

const historyReducer = (
  state: HistoryState,
  action: HistoryAction
): HistoryState => {
  switch (action.type) {
    case 'push': {
      if (state.stack[state.index] === action.key) return state
      const stack = [...state.stack.slice(0, state.index + 1), action.key]
      return { stack, index: stack.length - 1 }
    }
    case 'replace': {
      if (state.index < 0)
        return historyReducer(state, { type: 'push', key: action.key })
      const stack = [...state.stack]
      stack[state.index] = action.key
      return { ...state, stack }
    }
    case 'go': {
      const index = state.index + action.delta
      if (index < 0 || index >= state.stack.length) return state
      return { ...state, index }
    }
  }
}

const normalizeKey = (key: string) =>
  key === KNOWLEDGE_BASE_HOME
    ? KNOWLEDGE_BASE_HOME
    : formatKnowledgeBaseKey(parseKnowledgeBaseKey(key))

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))

export interface KnowledgeBaseContextType {
  isEnabled: boolean
  isOpen: boolean
  isDocked: boolean
  canDock: boolean
  isExpanded: boolean
  width: number
  current: string | null
  canGoBack: boolean
  canGoForward: boolean
  recent: string[]
  suggestions: string[]
  open: (key?: string) => void
  close: () => void
  toggle: () => void
  navigate: (key: string) => void
  replace: (key: string) => void
  goBack: () => void
  goForward: () => void
  setDocked: (docked: boolean) => void
  toggleExpanded: () => void
  setWidth: (width: number) => void
  setSuggestions: (keys: string[]) => void
}

const noop = () => {}

const KnowledgeBaseContext = createContext<KnowledgeBaseContextType>({
  isEnabled: false,
  isOpen: false,
  isDocked: false,
  canDock: false,
  isExpanded: false,
  width: KNOWLEDGE_BASE_DEFAULT_WIDTH,
  current: null,
  canGoBack: false,
  canGoForward: false,
  recent: [],
  suggestions: [],
  open: noop,
  close: noop,
  toggle: noop,
  navigate: noop,
  replace: noop,
  goBack: noop,
  goForward: noop,
  setDocked: noop,
  toggleExpanded: noop,
  setWidth: noop,
  setSuggestions: noop,
})

interface KnowledgeBaseProviderProps {
  children: ReactNode
}

export const KnowledgeBaseProvider = ({
  children,
}: KnowledgeBaseProviderProps) => {
  const isEnabled = useFeatureFlag(FLAGS.KNOWLEDGE_BASE_PANEL)
  const canDock = useCanDock()
  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [prefersDocked, setPrefersDocked] = dockedPreference.usePreference()
  const [width, setStoredWidth] = widthPreference.usePreference()
  const [recentRaw, setRecentRaw] = recentPreference.usePreference()
  const [history, dispatch] = useReducer(historyReducer, {
    stack: [],
    index: -1,
  })

  const recent = useMemo(
    () => recentRaw.split(',').filter(Boolean),
    [recentRaw]
  )

  const remember = useCallback(
    (key: string) => {
      const { path } = parseKnowledgeBaseKey(key)
      setRecentRaw(
        [
          path,
          ...recentPreference
            .read()
            .split(',')
            .filter((item) => item && item !== path),
        ]
          .slice(0, RECENT_LIMIT)
          .join(',')
      )
    },
    [setRecentRaw]
  )

  const navigate = useCallback(
    (key: string) => {
      const normalized = normalizeKey(key)
      if (normalized !== KNOWLEDGE_BASE_HOME) remember(normalized)
      dispatch({ type: 'push', key: normalized })
    },
    [remember]
  )

  const replace = useCallback(
    (key: string) => dispatch({ type: 'replace', key: normalizeKey(key) }),
    []
  )

  const open = useCallback(
    (key?: string) => {
      setIsOpen(true)
      if (key != null) navigate(key)
      else if (history.index < 0) navigate(KNOWLEDGE_BASE_HOME)
    },
    [history.index, navigate]
  )

  const close = useCallback(() => {
    setIsOpen(false)
    setIsExpanded(false)
  }, [])

  const toggle = useCallback(() => {
    if (isOpen) close()
    else open()
  }, [isOpen, open, close])

  const goBack = useCallback(() => dispatch({ type: 'go', delta: -1 }), [])
  const goForward = useCallback(() => dispatch({ type: 'go', delta: 1 }), [])

  const setDocked = useCallback(
    (docked: boolean) => {
      setPrefersDocked(docked)
      if (docked) setIsExpanded(false)
    },
    [setPrefersDocked]
  )

  const toggleExpanded = useCallback(
    () => setIsExpanded((expanded) => !expanded),
    []
  )

  const setWidth = useCallback(
    (next: number) => setStoredWidth(clampKnowledgeBaseWidth(next)),
    [setStoredWidth]
  )

  useEffect(() => {
    if (!isEnabled) return
    const onOpen = ({ key }: { key?: string }) => open(key)
    knowledgeBaseEvents.on('open', onOpen)
    knowledgeBaseEvents.on('close', close)
    knowledgeBaseEvents.on('toggle', toggle)
    return () => {
      knowledgeBaseEvents.off('open', onOpen)
      knowledgeBaseEvents.off('close', close)
      knowledgeBaseEvents.off('toggle', toggle)
    }
  }, [isEnabled, open, close, toggle])

  useEffect(() => {
    if (!isEnabled) return
    const key = new URLSearchParams(window.location.search).get(
      KNOWLEDGE_BASE_QUERY_PARAM
    )
    if (key != null) {
      setIsOpen(true)
      navigate(key)
    }
  }, [isEnabled, navigate])

  useEffect(() => {
    if (!isEnabled) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.altKey || event.shiftKey) return
      if (!(isApplePlatform() ? event.metaKey : event.ctrlKey)) return
      if (isTypingTarget(event.target) && !isOpen) return
      event.preventDefault()
      toggle()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isEnabled, isOpen, toggle])

  const value = useMemo<KnowledgeBaseContextType>(
    () => ({
      isEnabled,
      isOpen: isEnabled && isOpen,
      isDocked: prefersDocked && canDock,
      canDock,
      isExpanded,
      width,
      current: history.index >= 0 ? history.stack[history.index] : null,
      canGoBack: history.index > 0,
      canGoForward: history.index < history.stack.length - 1,
      recent,
      suggestions,
      open,
      close,
      toggle,
      navigate,
      replace,
      goBack,
      goForward,
      setDocked,
      toggleExpanded,
      setWidth,
      setSuggestions,
    }),
    [
      isEnabled,
      isOpen,
      prefersDocked,
      canDock,
      isExpanded,
      width,
      history,
      recent,
      suggestions,
      open,
      close,
      toggle,
      navigate,
      replace,
      goBack,
      goForward,
      setDocked,
      toggleExpanded,
      setWidth,
    ]
  )

  return (
    <KnowledgeBaseContext.Provider value={value}>
      {children}
    </KnowledgeBaseContext.Provider>
  )
}

export const useKnowledgeBase = (): KnowledgeBaseContextType =>
  useContext(KnowledgeBaseContext)

export const useKnowledgeBaseDockedWidth = (): number => {
  const { isOpen, isDocked, width } = useKnowledgeBase()
  return isOpen && isDocked ? width : 0
}

export const useKnowledgeBaseSuggestions = (keys: string[]): void => {
  const { setSuggestions } = useKnowledgeBase()
  const serialized = keys.join('|')
  useEffect(() => {
    setSuggestions(serialized ? serialized.split('|').map(normalizeKey) : [])
    return () => setSuggestions([])
  }, [serialized, setSuggestions])
}
